import React, { useEffect, useState, createContext, useContext } from 'react';
// Types
export interface ProspectingLead {
  id: string;
  prospectId?: string; // Added for compatibility
  allocatedAt?: string; // Added
  listDate?: string; // Added - for filtering by date
  zone: string;
  rooms: number;
  surface: number;
  floor: string;
  price: number;
  phone: string;
  source1: string;
  source2: string;
  source3: string;
  verificationStatus?: 'pending' | 'verified' | 'exists'; // Added
  callCompleted?: boolean; // Added
  callStatus?: string; // Added
  collaboration?: boolean; // Added - kept for backward compatibility
  propertyId?: string; // Added
  crmVerification?: string; // Kept for backward compatibility
  crmId?: string; // Kept for backward compatibility
  notes?: string; // Changed to optional
  listingId?: string;
  timeline?: Array<{
    action: string;
    date: string;
  }>;
}
export interface Listing {
  id: string; // Internal ID with P prefix (e.g., P10987)
  prospectingLeadId?: string;
  propertyType: string;
  zone: string;
  address: string;
  rooms: number;
  surface: number;
  floor: string;
  price: number;
  currency: string;
  status: 'draft' | 'active' | 'follow-up' | 'rented' | 'inactive';
  description: string;
  amenities: {
    furnished: string;
    heating: string;
    parking: boolean;
    balcony: boolean;
    pets: boolean;
  };
  images: string[];
  createdAt: string;
  activatedAt?: string; // When status changed to 'active'
  lastFollowUpAt?: string; // Last follow-up date
  ownerPhone: string;
  viewingsCount: number;
  leadsCount: number;
}
export interface TenantLead {
  id: string;
  name: string;
  phone: string;
  email: string;
  status: 'new' | 'qualified' | 'viewing' | 'offer' | 'closed' | 'rejected';
  budget: number;
  moveDate: string;
  rooms: number;
  preferredZones: string[];
  associatedListings: string[];
  viewingsCount: number;
  notes: string;
  createdAt: string;
  source: string;
  sourceListingId?: string;
}
export interface Viewing {
  id: string;
  // Lead/Client info
  leadId: string;
  clientName: string;
  clientPhone: string;
  // Property info
  propertyId: string;
  ownerName: string;
  ownerPhone: string;
  // Viewing details
  date: string;
  time: string;
  duration: number;
  status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
  meetingPoint: string;
  // Commission info
  commissionPaidBy: 'owner' | 'client' | 'both';
  // Legacy fields (kept for backward compatibility)
  listingId?: string;
  tenantLeadId?: string;
  agentNotes: string;
  createdAt: string;
  viewingType?: string;
  ownerCommission?: number;
  ownerVatOption?: 'included' | 'excluded';
  clientCommission?: number;
  clientVatOption?: 'included' | 'excluded';
  rescheduleReason?: string;
  rescheduledAt?: string;
  clientLikes?: boolean;
  completionFeedback?: string;
  nextStep?: string;
  completedAt?: string;
  signingDate?: string;
  signingTime?: string;
  signingLocation?: string;
  signingNotes?: string;
  signingScheduledAt?: string;
  signedAt?: string;
  ownerCommissionAmount?: number;
  totalCommission?: number;
  commissionNotes?: string;
  cancelReason?: string;
  cancelledAt?: string;
}
export interface Contract {
  id: string;
  type: 'landlord' | 'tenant';
  clientType: 'PF' | 'PJ';
  status: 'draft' | 'sent' | 'signed' | 'printed';
  createdAt: string;
  // Client data
  clientName: string;
  clientPhone: string;
  clientEmail: string;
  clientCNP?: string;
  clientCI?: string;
  clientAddress?: string;
  clientCUI?: string;
  clientCompanyName?: string;
  clientCompanyAddress?: string;
  clientRegCom?: string;
  clientLegalRep?: string;
  clientLegalRepPosition?: string;
  // Property data
  propertyAddress: string;
  propertyType?: string;
  rentalPrice: number;
  surface?: number;
  rooms?: number;
  guarantee?: number;
  // Commission data
  commissionPercent: number;
  vatIncluded: boolean;
  monthlyRent: number;
  commissionAmount: number;
  vatAmount: number;
  totalAmount: number;
  // Signing data
  clientSignature?: string;
  agentSignature?: string;
  signingLink?: string;
  signedAt?: string;
  // Associated records
  listingId?: string;
  tenantLeadId?: string;
}
interface RentalContextType {
  prospectingLeads: ProspectingLead[];
  addProspectingLead: (lead: ProspectingLead) => void;
  updateProspectingLead: (id: string, updates: Partial<ProspectingLead>) => void;
  importProspectingLeads: (leads: ProspectingLead[]) => void;
  listings: Listing[];
  addListing: (listing: Listing) => void;
  updateListing: (id: string, updates: Partial<Listing>) => void;
  getListingById: (id: string) => Listing | undefined;
  reactivateListing: (id: string) => void;
  tenantLeads: TenantLead[];
  addTenantLead: (lead: TenantLead) => void;
  updateTenantLead: (id: string, updates: Partial<TenantLead>) => void;
  getTenantLeadById: (id: string) => TenantLead | undefined;
  viewings: Viewing[];
  addViewing: (viewing: Viewing) => void;
  updateViewing: (id: string, updates: Partial<Viewing>) => void;
  getViewingsByListing: (listingId: string) => Viewing[];
  getViewingsByTenantLead: (tenantLeadId: string) => Viewing[];
  getStats: () => {
    totalProspectingLeads: number;
    newCollaborations: number;
    activeListings: number;
    activeTenantLeads: number;
    todayViewings: number;
  };
  contracts: Contract[];
  addContract: (contract: Contract) => void;
  updateContract: (id: string, updates: Partial<Contract>) => void;
  getContractById: (id: string) => Contract | undefined;
  getContractsByType: (type: 'landlord' | 'tenant') => Contract[];
}
const RentalContext = createContext<RentalContextType | undefined>(undefined);
// Generate internal ID with P prefix
const generateInternalId = (): string => {
  const randomNum = Math.floor(10000 + Math.random() * 90000);
  return `P${randomNum}`;
};
// Check if listing should be moved to follow-up (30 days after activation)
const shouldMoveToFollowUp = (listing: Listing): boolean => {
  if (listing.status !== 'active' || !listing.activatedAt) return false;
  const activatedDate = new Date(listing.activatedAt);
  const now = new Date();
  const daysDiff = Math.floor((now.getTime() - activatedDate.getTime()) / (1000 * 60 * 60 * 24));
  return daysDiff >= 30;
};
export function RentalProvider({
  children
}: {
  children: ReactNode;
}) {
  const today = new Date().toISOString().split('T')[0];
  // Generate 15 sample leads for today
  const generateSampleLeads = () => {
    const zones = ['Floreasca, București', 'Pipera, București', 'Aviatorilor, București', 'Dorobanți, București', 'Primăverii, București', 'Herastrau, București', 'Baneasa, București', 'Cotroceni, București', 'Universitate, București', 'Romana, București', 'Unirii, București', 'Vitan, București', 'Titan, București', 'Pantelimon, București', 'Militari, București'];
    return zones.map((zone, index) => ({
      id: `LEAD${String(index + 1).padStart(4, '0')}`,
      prospectId: `LEAD${String(index + 1).padStart(4, '0')}`,
      allocatedAt: new Date().toISOString(),
      listDate: today,
      zone: zone,
      rooms: Math.floor(Math.random() * 3) + 1,
      surface: Math.floor(Math.random() * 50) + 40,
      floor: `${Math.floor(Math.random() * 10) + 1}`,
      price: Math.floor(Math.random() * 500) + 300,
      phone: `072${Math.floor(Math.random() * 9000000) + 1000000}`,
      source1: `https://www.imobiliare.ro/inchirieri-apartamente/${zone.toLowerCase().replace(/[, ]/g, '-')}/`,
      source2: '',
      source3: '',
      verificationStatus: 'pending' as const,
      callCompleted: false,
      callStatus: 'de-apelat',
      collaboration: false,
      propertyId: '',
      notes: '',
      timeline: []
    }));
  };
  const [prospectingLeads, setProspectingLeads] = useState<ProspectingLead[]>(() => {
    // Initialize with 15 sample leads
    return generateSampleLeads();
  });
  const [listings, setListings] = useState<Listing[]>([{
    id: 'P14433',
    prospectingLeadId: '1',
    propertyType: 'apartament',
    zone: 'Titan',
    address: 'Str. Titan 45, Sector 3, București',
    rooms: 2,
    surface: 52,
    floor: '3/4',
    price: 450,
    currency: 'EUR',
    status: 'active',
    description: 'Apartament modern, renovat recent, zona linistita',
    amenities: {
      furnished: 'mobilat',
      heating: 'centrala',
      parking: true,
      balcony: true,
      pets: false
    },
    images: [],
    createdAt: '2024-01-15T11:45:00',
    activatedAt: '2024-01-15T11:45:00',
    ownerPhone: '0721234567',
    viewingsCount: 2,
    leadsCount: 3
  }]);
  const [tenantLeads, setTenantLeads] = useState<TenantLead[]>([{
    id: 'TL001',
    name: 'Maria Ionescu',
    phone: '0732345678',
    email: 'maria.ionescu@email.com',
    status: 'viewing',
    budget: 500,
    moveDate: '2024-02-01',
    rooms: 2,
    preferredZones: ['Titan', 'Pantelimon'],
    associatedListings: ['P14433'],
    viewingsCount: 1,
    notes: 'Clientă serioasă, preferă apartamente luminoase',
    createdAt: '2024-01-14T10:30:00',
    source: 'listing_inquiry',
    sourceListingId: 'P14433'
  }]);
  const [viewings, setViewings] = useState<Viewing[]>([
  // 1. Scheduled - Programată pentru mâine
  {
    id: 'V001',
    leadId: 'TL001',
    clientName: 'Maria Ionescu',
    clientPhone: '0732345678',
    propertyId: 'P14433',
    ownerName: 'Ion Popescu',
    ownerPhone: '0721234567',
    date: new Date(Date.now() + 86400000).toISOString().split('T')[0],
    time: '14:00',
    duration: 30,
    status: 'scheduled',
    meetingPoint: 'La intrarea în bloc',
    commissionPaidBy: 'owner',
    listingId: 'P14433',
    tenantLeadId: 'TL001',
    agentNotes: 'Clienta preferă apartamente luminoase',
    createdAt: new Date(Date.now() - 86400000).toISOString(),
    viewingType: 'inchiriere',
    ownerCommission: 50,
    ownerVatOption: 'included'
  },
  // 2. Rescheduled - Reprogramată
  {
    id: 'V002',
    leadId: 'TL002',
    clientName: 'Andrei Popescu',
    clientPhone: '0745123456',
    propertyId: 'P14433',
    ownerName: 'Ion Popescu',
    ownerPhone: '0721234567',
    date: new Date(Date.now() + 172800000).toISOString().split('T')[0],
    time: '16:00',
    duration: 30,
    status: 'rescheduled',
    meetingPoint: 'La proprietate',
    commissionPaidBy: 'both',
    listingId: 'P14433',
    tenantLeadId: 'TL002',
    agentNotes: 'Client serios, reprogramat din cauza unei urgențe',
    createdAt: new Date(Date.now() - 172800000).toISOString(),
    viewingType: 'inchiriere',
    ownerCommission: 50,
    ownerVatOption: 'included',
    clientCommission: 50,
    clientVatOption: 'included',
    rescheduleReason: 'Clientul a avut o urgență la serviciu',
    rescheduledAt: new Date(Date.now() - 86400000).toISOString()
  },
  // 3. NEEDS FEEDBACK - Scheduled but past time (YESTERDAY 10:00)
  {
    id: 'V003',
    leadId: 'TL003',
    clientName: 'Elena Dumitrescu',
    clientPhone: '0756789012',
    propertyId: 'P14433',
    ownerName: 'Ion Popescu',
    ownerPhone: '0721234567',
    date: new Date(Date.now() - 86400000).toISOString().split('T')[0],
    time: '10:00',
    duration: 30,
    status: 'scheduled',
    meetingPoint: 'La proprietate',
    commissionPaidBy: 'owner',
    listingId: 'P14433',
    tenantLeadId: 'TL003',
    agentNotes: 'Vizionare programată ieri, necesită feedback',
    createdAt: new Date(Date.now() - 259200000).toISOString(),
    viewingType: 'inchiriere',
    ownerCommission: 50,
    ownerVatOption: 'included'
  },
  // 4. Awaiting Response - Finalizată, aștept răspuns
  {
    id: 'V004',
    leadId: 'TL004',
    clientName: 'Mihai Georgescu',
    clientPhone: '0767890123',
    propertyId: 'P14433',
    ownerName: 'Ion Popescu',
    ownerPhone: '0721234567',
    date: new Date(Date.now() - 172800000).toISOString().split('T')[0],
    time: '15:00',
    duration: 30,
    status: 'awaiting_response',
    meetingPoint: 'La proprietate',
    commissionPaidBy: 'client',
    listingId: 'P14433',
    tenantLeadId: 'TL004',
    agentNotes: 'Clienta vrea să consulte familia',
    createdAt: new Date(Date.now() - 345600000).toISOString(),
    viewingType: 'inchiriere',
    clientCommission: 50,
    clientVatOption: 'additional',
    clientLikes: true,
    completionFeedback: 'Clienta este interesată dar vrea să discute cu soțul. Urmează să ne contacteze în 2 zile.',
    nextStep: 'awaiting_response',
    completedAt: new Date(Date.now() - 86400000).toISOString()
  },
  // 5. Signing Scheduled - Semnare programată
  {
    id: 'V005',
    leadId: 'TL005',
    clientName: 'Alexandra Stan',
    clientPhone: '0778901234',
    propertyId: 'P14433',
    ownerName: 'Ion Popescu',
    ownerPhone: '0721234567',
    date: new Date(Date.now() - 259200000).toISOString().split('T')[0],
    time: '12:00',
    duration: 30,
    status: 'signing_scheduled',
    meetingPoint: 'La birou',
    commissionPaidBy: 'both',
    listingId: 'P14433',
    tenantLeadId: 'TL005',
    agentNotes: 'Client foarte interesat, dorește să semneze rapid',
    createdAt: new Date(Date.now() - 432000000).toISOString(),
    viewingType: 'inchiriere',
    ownerCommission: 50,
    ownerVatOption: 'included',
    clientCommission: 50,
    clientVatOption: 'included',
    clientLikes: true,
    completionFeedback: 'Clientul dorește să programeze semnarea contractului.',
    nextStep: 'schedule_signing',
    completedAt: new Date(Date.now() - 172800000).toISOString(),
    signingDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
    signingTime: '11:00',
    signingLocation: 'La birou',
    signingNotes: 'Clientul va veni cu actele necesare',
    signingScheduledAt: new Date(Date.now() - 43200000).toISOString()
  },
  // 6. Signed - Invoicing - Semnat, aștept facturare
  {
    id: 'V006',
    leadId: 'TL006',
    clientName: 'Cristian Marin',
    clientPhone: '0789012345',
    propertyId: 'P14433',
    ownerName: 'Ion Popescu',
    ownerPhone: '0721234567',
    date: new Date(Date.now() - 432000000).toISOString().split('T')[0],
    time: '10:00',
    duration: 30,
    status: 'signed_invoicing',
    meetingPoint: 'La birou',
    commissionPaidBy: 'owner',
    listingId: 'P14433',
    tenantLeadId: 'TL006',
    agentNotes: 'Contract semnat, pregătesc factura',
    createdAt: new Date(Date.now() - 604800000).toISOString(),
    viewingType: 'inchiriere',
    ownerCommission: 50,
    ownerVatOption: 'included',
    propertyPrice: 450,
    clientLikes: true,
    completionFeedback: 'Clientul dorește să programeze semnarea contractului.',
    nextStep: 'schedule_signing',
    completedAt: new Date(Date.now() - 345600000).toISOString(),
    signingDate: new Date(Date.now() - 172800000).toISOString().split('T')[0],
    signingTime: '14:00',
    signingLocation: 'La birou',
    signingScheduledAt: new Date(Date.now() - 259200000).toISOString(),
    signedAt: new Date(Date.now() - 86400000).toISOString()
  },
  // 7. Commission Collected - Comision încasat (finalizată)
  {
    id: 'V007',
    leadId: 'TL007',
    clientName: 'Diana Vasile',
    clientPhone: '0790123456',
    propertyId: 'P14433',
    ownerName: 'Ion Popescu',
    ownerPhone: '0721234567',
    date: new Date(Date.now() - 604800000).toISOString().split('T')[0],
    time: '10:00',
    duration: 30,
    status: 'commission_collected',
    meetingPoint: 'La proprietate',
    commissionPaidBy: 'owner',
    listingId: 'P14433',
    tenantLeadId: 'TL007',
    agentNotes: 'Tranzacție finalizată cu succes',
    createdAt: new Date(Date.now() - 864000000).toISOString(),
    viewingType: 'inchiriere',
    ownerCommission: 50,
    ownerVatOption: 'included',
    propertyPrice: 450,
    clientLikes: true,
    completionFeedback: 'Clientul dorește să programeze semnarea contractului.',
    nextStep: 'schedule_signing',
    completedAt: new Date(Date.now() - 518400000).toISOString(),
    signingDate: new Date(Date.now() - 345600000).toISOString().split('T')[0],
    signingTime: '15:00',
    signingLocation: 'La birou',
    signingScheduledAt: new Date(Date.now() - 432000000).toISOString(),
    signedAt: new Date(Date.now() - 259200000).toISOString(),
    ownerCommissionAmount: 225,
    totalCommission: 225,
    commissionNotes: 'Plată efectuată prin transfer bancar',
    commissionCollectedAt: new Date(Date.now() - 172800000).toISOString()
  },
  // 8. Cancelled - Anulată
  {
    id: 'V008',
    leadId: 'TL008',
    clientName: 'Florin Popa',
    clientPhone: '0701234567',
    propertyId: 'P14433',
    ownerName: 'Ion Popescu',
    ownerPhone: '0721234567',
    date: new Date(Date.now() - 432000000).toISOString().split('T')[0],
    time: '17:00',
    duration: 30,
    status: 'cancelled',
    meetingPoint: 'La proprietate',
    commissionPaidBy: 'owner',
    listingId: 'P14433',
    tenantLeadId: 'TL008',
    agentNotes: 'Clienta a găsit altă proprietate',
    createdAt: new Date(Date.now() - 518400000).toISOString(),
    viewingType: 'inchiriere',
    ownerCommission: 50,
    ownerVatOption: 'included',
    cancelReason: 'Clienta a găsit o proprietate mai apropiată de serviciu',
    cancelledAt: new Date(Date.now() - 345600000).toISOString()
  },
  // 9. Completed (Nu îi place) - Finalizată, rematching
  {
    id: 'V009',
    leadId: 'TL009',
    clientName: 'George Ionescu',
    clientPhone: '0712345678',
    propertyId: 'P14433',
    ownerName: 'Ion Popescu',
    ownerPhone: '0721234567',
    date: new Date(Date.now() - 345600000).toISOString().split('T')[0],
    time: '11:00',
    duration: 30,
    status: 'completed',
    meetingPoint: 'La proprietate',
    commissionPaidBy: 'owner',
    listingId: 'P14433',
    tenantLeadId: 'TL009',
    agentNotes: 'Client căuta ceva mai spațios',
    createdAt: new Date(Date.now() - 432000000).toISOString(),
    viewingType: 'inchiriere',
    ownerCommission: 50,
    ownerVatOption: 'included',
    clientLikes: false,
    completionFeedback: 'Clientul consideră că apartamentul este prea mic. Caută ceva cu 3 camere în aceeași zonă.',
    nextStep: 'rematching',
    completedAt: new Date(Date.now() - 259200000).toISOString()
  },
  // 10. NEEDS FEEDBACK #2 - Rescheduled but past time (2 HOURS AGO)
  {
    id: 'V010',
    leadId: 'TL010',
    clientName: 'Raluca Popescu',
    clientPhone: '0723456789',
    propertyId: 'P14433',
    ownerName: 'Ion Popescu',
    ownerPhone: '0721234567',
    date: new Date().toISOString().split('T')[0],
    time: new Date(Date.now() - 7200000).toLocaleTimeString('ro-RO', {
      hour: '2-digit',
      minute: '2-digit'
    }),
    duration: 30,
    status: 'rescheduled',
    meetingPoint: 'La proprietate',
    commissionPaidBy: 'both',
    listingId: 'P14433',
    tenantLeadId: 'TL010',
    agentNotes: 'Vizionare reprogramată, a trecut timpul',
    createdAt: new Date(Date.now() - 259200000).toISOString(),
    viewingType: 'inchiriere',
    ownerCommission: 50,
    ownerVatOption: 'included',
    clientCommission: 50,
    clientVatOption: 'included',
    rescheduleReason: 'Clienta a întârziat',
    rescheduledAt: new Date(Date.now() - 86400000).toISOString()
  }]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  // Auto-check listings for follow-up status every minute
  useEffect(() => {
    const checkListingsStatus = () => {
      setListings(prevListings => prevListings.map(listing => {
        if (shouldMoveToFollowUp(listing)) {
          return {
            ...listing,
            status: 'follow-up' as const
          };
        }
        return listing;
      }));
    };
    // Check immediately on mount
    checkListingsStatus();
    // Check every minute
    const interval = setInterval(checkListingsStatus, 60000);
    return () => clearInterval(interval);
  }, []);
  const addProspectingLead = (lead: ProspectingLead) => {
    setProspectingLeads([...prospectingLeads, lead]);
  };
  const updateProspectingLead = (id: string, updates: Partial<ProspectingLead>) => {
    setProspectingLeads(prospectingLeads.map(lead => lead.id === id ? {
      ...lead,
      ...updates
    } : lead));
  };
  const importProspectingLeads = (leads: ProspectingLead[]) => {
    // Replace existing leads with new ones, or append?
    // The requirement says "Clear existing mock data when importing new list"
    // We'll keep historical leads (if we had a way to distinguish) but for now let's replace the current view's data
    // or just append them. Given the "mock data" comment, replacing seems safer for a clean state.
    setProspectingLeads(leads);
  };
  const addListing = (listing: Listing) => {
    // Generate internal ID if not provided
    const listingWithId = {
      ...listing,
      id: listing.id || generateInternalId(),
      // Set activatedAt when status is 'active'
      activatedAt: listing.status === 'active' ? new Date().toISOString() : undefined
    };
    setListings([...listings, listingWithId]);
    // Update prospecting lead if linked
    if (listingWithId.prospectingLeadId) {
      updateProspectingLead(listingWithId.prospectingLeadId, {
        listingId: listingWithId.id,
        crmId: listingWithId.id,
        crmVerification: 'exclusivitate'
      });
    }
  };
  const updateListing = (id: string, updates: Partial<Listing>) => {
    setListings(listings.map(listing => {
      if (listing.id === id) {
        const updatedListing = {
          ...listing,
          ...updates
        };
        // If status changes to 'active', set activatedAt
        if (updates.status === 'active' && listing.status !== 'active') {
          updatedListing.activatedAt = new Date().toISOString();
        }
        return updatedListing;
      }
      return listing;
    }));
  };
  const getListingById = (id: string) => {
    return listings.find(listing => listing.id === id);
  };
  const reactivateListing = (id: string) => {
    setListings(listings.map(listing => {
      if (listing.id === id && listing.status === 'follow-up') {
        return {
          ...listing,
          status: 'active' as const,
          activatedAt: new Date().toISOString(),
          lastFollowUpAt: new Date().toISOString()
        };
      }
      return listing;
    }));
  };
  const addTenantLead = (lead: TenantLead) => {
    setTenantLeads([...tenantLeads, lead]);
    if (lead.associatedListings.length > 0) {
      lead.associatedListings.forEach(listingId => {
        const listing = listings.find(l => l.id === listingId);
        if (listing) {
          updateListing(listingId, {
            leadsCount: listing.leadsCount + 1
          });
        }
      });
    }
  };
  const updateTenantLead = (id: string, updates: Partial<TenantLead>) => {
    setTenantLeads(tenantLeads.map(lead => lead.id === id ? {
      ...lead,
      ...updates
    } : lead));
  };
  const getTenantLeadById = (id: string) => {
    return tenantLeads.find(lead => lead.id === id);
  };
  const addViewing = (viewing: Viewing) => {
    setViewings([...viewings, viewing]);
    const listing = listings.find(l => l.id === viewing.listingId);
    if (listing) {
      updateListing(viewing.listingId, {
        viewingsCount: listing.viewingsCount + 1
      });
    }
    const tenantLead = tenantLeads.find(l => l.id === viewing.tenantLeadId);
    if (tenantLead) {
      updateTenantLead(viewing.tenantLeadId, {
        viewingsCount: tenantLead.viewingsCount + 1,
        status: 'viewing'
      });
    }
  };
  const updateViewing = (id: string, updates: Partial<Viewing>) => {
    setViewings(viewings.map(viewing => viewing.id === id ? {
      ...viewing,
      ...updates
    } : viewing));
  };
  const getViewingsByListing = (listingId: string) => {
    return viewings.filter(v => v.listingId === listingId);
  };
  const getViewingsByTenantLead = (tenantLeadId: string) => {
    return viewings.filter(v => v.tenantLeadId === tenantLeadId);
  };
  const getStats = () => {
    const today = new Date().toISOString().split('T')[0];
    return {
      totalProspectingLeads: prospectingLeads.length,
      newCollaborations: prospectingLeads.filter(l => l.collaboration).length,
      activeListings: listings.filter(l => l.status === 'active').length,
      activeTenantLeads: tenantLeads.filter(l => ['new', 'qualified', 'viewing', 'offer'].includes(l.status)).length,
      todayViewings: viewings.filter(v => v.date === today).length
    };
  };
  const addContract = (contract: Contract) => {
    setContracts([...contracts, contract]);
  };
  const updateContract = (id: string, updates: Partial<Contract>) => {
    setContracts(contracts.map(contract => contract.id === id ? {
      ...contract,
      ...updates
    } : contract));
  };
  const getContractById = (id: string) => {
    return contracts.find(contract => contract.id === id);
  };
  const getContractsByType = (type: 'landlord' | 'tenant') => {
    return contracts.filter(contract => contract.type === type);
  };
  const value = {
    prospectingLeads,
    addProspectingLead,
    updateProspectingLead,
    importProspectingLeads,
    listings,
    addListing,
    updateListing,
    getListingById,
    reactivateListing,
    tenantLeads,
    addTenantLead,
    updateTenantLead,
    getTenantLeadById,
    viewings,
    addViewing,
    updateViewing,
    getViewingsByListing,
    getViewingsByTenantLead,
    getStats,
    contracts,
    addContract,
    updateContract,
    getContractById,
    getContractsByType
  };
  return <RentalContext.Provider value={value} data-id="element-7102">{children}</RentalContext.Provider>;
}
export function useRental() {
  const context = useContext(RentalContext);
  if (context === undefined) {
    throw new Error('useRental must be used within a RentalProvider');
  }
  return context;
}